import Header from 'components/headers/bulletins'
import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import posthog from 'posthog-js'
import useGetInitBanks from './hooks/useGetInitBanks'
import useGetPrice from './hooks/useGetPrice'
import DotsLoading from 'components/loadings/dotsLoading'
import useSetPayment from './hooks/useSetPayment'
import useFullDiscountOrder from './hooks/useFullDiscountOrder'
import useDiscount, { payableAfterDiscount } from './hooks/useDiscount'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { toggleSupportModal } from 'store/redux/sidebar'
import { useDispatch, useSelector } from 'react-redux'
import SupportModal from 'components/modals/supportModal'
import { RootState } from 'store/redux'
import {
    getActiveGateways,
    useActiveGatewayCode,
} from 'utilities/hooks/useIsDefaultGateway'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'
import { PLAN_SUBSCRIPTION } from 'pages/fund/buySubscription/utils'
import { faPlain } from 'utilities/helper/format'

const toman = (amount?: number) =>
    amount === undefined ? '—' : `${amount.toLocaleString('fa-IR')} تومان`

const faNum = (value: number) => value.toLocaleString('fa-IR')

function BillRow({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'discount' | 'total'
}) {
    return (
        <div className=" flex items-center justify-between gap-2 border-b border-divider py-2.5 last:border-b-0">
            <span className=" text-xs text-text-secondary">{label}</span>
            <span
                className={classNames(
                    'text-sm',
                    tone === 'discount' && ' text-brand-deep',
                    tone === 'total' && ' font-bold text-heading',
                    !tone && ' text-text-body'
                )}
            >
                {value}
            </span>
        </div>
    )
}

export default function Subscription() {
    const [selectedCode, setSelectedCode] = useState<string | null>(null)
    const [monthCount, setMonthCount] = useState<number | null>(null)
    const [codeInput, setCodeInput] = useState('')

    const { data, isLoading } = useGetInitBanks()
    const { mutate: pay, isLoading: isPaying } = useSetPayment()
    const { moneyPool } = useParams()
    const { mutate: activateFree, isLoading: isActivating } =
        useFullDiscountOrder(moneyPool)
    const discount = useDiscount()
    const dispatch = useDispatch()
    const { isOpenSupportModal } = useSelector(
        (state: RootState) => state.sideBarControler
    )

    useActiveGatewayCode(data, setSelectedCode)

    // Renewing an unexpired subscription: the backend stacks the periods
    // (`calculate_order_end_date` carries the leftover days onto the new
    // order), so say so here rather than leaving the manager to guess whether
    // paying early throws the rest of the month away.
    const { moneyPoolManagement } = useGetMoneyPoolManagement(moneyPool)
    const remainingDays = moneyPoolManagement?.service_package_remaining_days
    const isRenewal =
        moneyPoolManagement?.service_package === PLAN_SUBSCRIPTION &&
        typeof remainingDays === 'number' &&
        remainingDays > 0

    // Prices are per 50 active members and differ per fund, so each card reads
    // its amount from the server rather than from a hardcoded label.
    const monthly = useGetPrice(moneyPool, 1)
    const threeMonthly = useGetPrice(moneyPool, 3)
    const isPriceLoading = monthly.isLoading || threeMonthly.isLoading

    // An interval with no price configured comes back as 0. Showing it as a
    // «۰ تومان» card would just fail at the gateway, so leave it out.
    const plans = [
        { months: 1, title: 'یک ماهه', price: monthly.price },
        { months: 3, title: 'سه ماهه', price: threeMonthly.price },
    ].filter((plan) => !!plan.price && plan.price > 0)

    // Wait for BOTH price queries before picking a default. Keying this off
    // `plans.length` alone made the default whichever request happened to land
    // first — if the 3-month price arrived first it became the only entry, got
    // selected, and then stayed selected once the 1-month price filled in.
    useEffect(() => {
        if (isPriceLoading) return
        if (plans.length && !plans.some((p) => p.months === monthCount)) {
            setMonthCount(plans[0].months)
        }
    }, [isPriceLoading, plans.length, monthCount]) // eslint-disable-line react-hooks/exhaustive-deps

    const basePrice = plans.find((p) => p.months === monthCount)?.price
    const percentage = discount.applied?.percentage ?? 0
    const payable =
        basePrice === undefined
            ? undefined
            : payableAfterDiscount(basePrice, percentage)
    const discountAmount =
        basePrice !== undefined && payable !== undefined
            ? basePrice - payable
            : 0

    // Nothing left to pay: the gateway is irrelevant and the purchase settles
    // through the order path instead.
    const isFullDiscount = payable === 0 && !!discount.applied
    const isBusy = isPaying || isActivating

    // Once, after the prices and the gateway list have both landed. Firing on
    // mount would report «۰ بسته، ۰ درگاه» for every visitor, which is the
    // loading state and not the screen.
    const viewed = useRef(false)
    useEffect(() => {
        if (viewed.current || isPriceLoading || isLoading) return
        viewed.current = true
        posthog.capture('subscription_page_viewed', {
            box_id: moneyPool,
            box_type: 'mnp',
            is_renewal: isRenewal,
            remaining_days: remainingDays ?? null,
            plans_available: plans.length,
            gateways_available: getActiveGateways(data).length,
        })
    }, [isPriceLoading, isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        if (!basePrice || !monthCount || payable === undefined) {
            toast.error('مبلغ اشتراک در دسترس نیست. لطفاً دوباره تلاش کنید.')
            return
        }
        if (isFullDiscount) {
            activateFree({
                ctxId: Number(moneyPool),
                monthCount,
                discountCode: discount.applied!.code,
            })
            return
        }
        if (!selectedCode) {
            toast.error('روش پرداخت را انتخاب کنید')
            return
        }
        posthog.capture('subscription_purchase_initiated', {
            flow_type: 'subscription',
            amount: payable,
            base_price: basePrice,
            month_count: monthCount,
            moneypool_id: moneyPool,
            gateway: selectedCode,
            has_discount: !!discount.applied,
            discount_percentage: percentage,
            is_renewal: isRenewal,
        })
        pay({
            gatewayCode: selectedCode,
            monthCount,
            ctxId: Number(moneyPool),
            amount: payable,
            discountCode: discount.applied?.code ?? null,
        })
    }

    return (
        <>
            <div className=" flex min-h-screen flex-col bg-canvas">
                <div className=" grow-0">
                    <Header
                        title={isRenewal ? 'تمدید اشتراک' : 'خرید اشتراک'}
                    />
                </div>

                <div className=" flex grow flex-col px-4 pb-6">
                    {isRenewal && (
                        <p className=" mt-4 rounded-card bg-brand-soft p-3 text-sm leading-6 text-brand-deep">
                            اشتراک فعلی صندوق {faPlain.format(remainingDays!)}{' '}
                            روز دیگر اعتبار دارد. این روزها از دست نمی‌رود و به
                            دوره‌ای که خریداری می‌کنید اضافه می‌شود.
                        </p>
                    )}
                    {isPriceLoading && (
                        <div className=" py-6">
                            <DotsLoading />
                        </div>
                    )}

                    {!isPriceLoading && !plans.length && (
                        <p className=" mt-6 rounded-card bg-danger-soft p-3 text-center text-sm leading-6 text-danger-on">
                            در حال حاضر امکان خرید اشتراک برای این صندوق وجود
                            ندارد. لطفاً با مشاور همیان تماس بگیرید.
                        </p>
                    )}

                    {!!plans.length && (
                        <>
                            <h2 className=" mt-4 text-sm font-bold text-heading">
                                مدت اشتراک
                            </h2>
                            <div className=" mt-2 grid grid-cols-2 gap-2">
                                {plans.map((plan) => {
                                    const isSelected =
                                        monthCount === plan.months
                                    return (
                                        <button
                                            key={plan.months}
                                            type="button"
                                            aria-pressed={isSelected}
                                            onClick={() =>
                                                setMonthCount(plan.months)
                                            }
                                            className={classNames(
                                                'rounded-card border px-3 py-3 text-right transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                                                isSelected
                                                    ? ' border-brand bg-brand-soft'
                                                    : ' border-stroke bg-white'
                                            )}
                                        >
                                            <span
                                                className={classNames(
                                                    'block text-sm font-bold',
                                                    isSelected
                                                        ? ' text-brand-deep'
                                                        : ' text-text-body'
                                                )}
                                            >
                                                {plan.title}
                                            </span>
                                            <span
                                                className={classNames(
                                                    'mt-1 block text-xs',
                                                    isSelected
                                                        ? ' text-brand-deep'
                                                        : ' text-text-secondary'
                                                )}
                                            >
                                                {toman(plan.price)}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            {/* Discount code */}
                            <h2 className=" mt-5 text-sm font-bold text-heading">
                                کد تخفیف
                            </h2>
                            {discount.applied ? (
                                <div className=" mt-2 flex items-center justify-between gap-2 rounded-card border border-brand bg-brand-soft px-3 py-2.5">
                                    <span className=" text-xs text-brand-deep">
                                        {faNum(discount.applied.percentage)}٪
                                        تخفیف با کد{' '}
                                        <span className=" font-bold">
                                            {discount.applied.code}
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        className=" shrink-0 text-xs font-bold text-danger-on underline underline-offset-4"
                                        onClick={() => {
                                            discount.clear()
                                            setCodeInput('')
                                        }}
                                    >
                                        حذف
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className=" mt-2 flex gap-2">
                                        <input
                                            value={codeInput}
                                            onChange={(e) =>
                                                setCodeInput(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter')
                                                    discount.apply(codeInput)
                                            }}
                                            placeholder="کد تخفیف دارید؟"
                                            aria-label="کد تخفیف"
                                            className=" h-11 grow rounded-card border border-stroke bg-white px-3 text-sm text-text-body placeholder:text-text-muted focus:border-brand focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            disabled={
                                                !codeInput.trim() ||
                                                discount.isChecking
                                            }
                                            onClick={() =>
                                                discount.apply(codeInput)
                                            }
                                            className=" h-11 shrink-0 rounded-card border border-brand px-4 text-sm font-bold text-brand-deep disabled:opacity-40"
                                        >
                                            {discount.isChecking
                                                ? '…'
                                                : 'اعمال'}
                                        </button>
                                    </div>
                                    {discount.error && (
                                        <p className=" mt-1.5 text-xs text-danger-on">
                                            {discount.error}
                                        </p>
                                    )}
                                </>
                            )}

                            {/* Bill */}
                            <h2 className=" mt-5 text-sm font-bold text-heading">
                                صورت‌حساب
                            </h2>
                            <div className=" mt-2 rounded-card bg-white px-3 shadow-card">
                                <BillRow
                                    label="ارزش بسته اشتراک"
                                    value={toman(basePrice)}
                                />
                                {!!discount.applied && (
                                    // No leading «−»: a lone minus in an RTL
                                    // line renders on the far side of the
                                    // number and reads as if it were trailing.
                                    // The label and the green already say it is
                                    // a deduction.
                                    <BillRow
                                        label={`تخفیف (${faNum(
                                            discount.applied.percentage
                                        )}٪)`}
                                        value={toman(discountAmount)}
                                        tone="discount"
                                    />
                                )}
                                <BillRow
                                    label="مبلغ قابل پرداخت"
                                    value={toman(payable)}
                                    tone="total"
                                />
                            </div>

                            {/* Gateway — pointless when there is nothing to pay */}
                            {!isFullDiscount && (
                                <>
                                    <h2 className=" mt-5 text-sm font-bold text-heading">
                                        روش پرداخت
                                    </h2>
                                    {!isLoading && data ? (
                                        <div className=" mt-2 flex flex-wrap gap-3">
                                            {getActiveGateways(data).map(
                                                (gw) => (
                                                    <button
                                                        key={gw.unique_code}
                                                        type="button"
                                                        aria-pressed={
                                                            selectedCode ===
                                                            gw.unique_code
                                                        }
                                                        onClick={() =>
                                                            setSelectedCode(
                                                                gw.unique_code
                                                            )
                                                        }
                                                        className={classNames(
                                                            'rounded-card border bg-white p-1 transition-colors',
                                                            selectedCode ===
                                                                gw.unique_code
                                                                ? ' border-brand'
                                                                : ' border-stroke'
                                                        )}
                                                    >
                                                        <img
                                                            src={gw.img_url}
                                                            alt={gw.unique_code}
                                                            className=" h-12"
                                                        />
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <DotsLoading />
                                    )}
                                </>
                            )}
                        </>
                    )}

                    <div className=" mt-auto flex flex-col items-center pb-4 pt-8 text-center">
                        <p className=" text-xs text-text-secondary">
                            نیاز به راهنمایی دارید؟
                        </p>
                        <button
                            type="button"
                            className=" mt-1 text-sm font-bold text-brand-deep underline underline-offset-4"
                            onClick={() =>
                                dispatch(
                                    toggleSupportModal({
                                        isOpenSupportModal: true,
                                    })
                                )
                            }
                        >
                            با مشاور همیان تماس بگیرید
                        </button>
                    </div>
                </div>

                <div className=" grow-0 border-t border-divider bg-white px-4 py-3">
                    <button
                        type="button"
                        className=" w-full rounded-card bg-brand py-3 text-sm font-bold text-white transition-colors active:bg-brand-strong disabled:opacity-50"
                        disabled={isBusy || !plans.length}
                        onClick={handleSubmit}
                    >
                        {isBusy
                            ? isFullDiscount
                                ? 'در حال فعال‌سازی…'
                                : 'در حال انتقال به درگاه…'
                            : isFullDiscount
                            ? 'فعال‌سازی اشتراک'
                            : `پرداخت ${toman(payable)}`}
                    </button>
                </div>
            </div>

            <SupportModal
                show={isOpenSupportModal}
                onHide={() =>
                    dispatch(toggleSupportModal({ isOpenSupportModal: false }))
                }
            />
        </>
    )
}
